import React, { Fragment, startTransition, useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get, isEqual } from 'lodash-es';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import Button from 'reactstrap/lib/Button';
import { useToggle } from 'site-modules/shared/hooks/use-toggle';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import {
  buildNewTmvAdjustmentPath,
  buildNewTmvPricingPath,
  buildVehiclePath,
  VehicleEntities,
  VehicleModel,
} from 'client/data/models/vehicle-v2';
import { buildClosestDealersPath, DealershipsModel } from 'client/data/models/dealerships';
import {
  DROPDOWN_ERRORS,
  MAKES_WITH_DEFAULT_MSRP,
  PRICE_CHECKER_EVENTS,
  PriceCheckerEntities,
  VEHICLE_SELECT_MODE_MAP,
} from 'site-modules/shared/constants/price-checker';
import { PUB_STATES } from 'client/constants/pub-states';
import { objectToQueryString } from 'site-modules/shared/utils/string';
import { isUsedState } from 'site-modules/shared/utils/publication-states';
import { DEALERLESS_OEM, DEALERLESS_OEM_TRIAL } from 'site-modules/shared/constants/oem';
import { getModelIncentivesLink } from 'site-modules/shared/utils/incentives-link-constructor';
import { submitDeal as submitShareDeal } from 'site-modules/shared/utils/price-checker/community-share-utils';
import { getTmvPrices } from 'site-modules/shared/utils/price-checker';
import { EventToolbox } from 'client/utils/event-toolbox';
import { VehicleDropdowns } from 'site-modules/shared/components/vehicle-dropdowns/vehicle-dropdowns';
import { StyledStyleDropdown } from 'site-modules/shared/components/vehicle-dropdowns/style-dropdown';
import { PriceCheckerChart } from 'site-modules/shared/components/price-checker/price-checker-chart/price-checker-chart';
import { PricesForm } from 'site-modules/price-checker/components/prices-form/prices-form';
import { PriceCheckerTaxesFees } from 'site-modules/price-checker/components/price-checker-taxes-fees/price-checker-taxes-fees';
import { GoToAppraiseModal } from 'site-modules/price-checker/components/go-to-appraise-modal/go-to-appraise-modal';
import { PriceCheckerInfo } from 'site-modules/price-checker/components/price-checker-info/price-checker-info';
import { ModeSelect } from 'site-modules/price-checker/components/mode-select/mode-select';
import { Link } from 'site-modules/shared/components/link/link';
import { useDebounce } from 'site-modules/shared/hooks/use-debounce';

import './style-price-checker.scss';

const USED_YEAR = 'Used';
const VEHICLE_DROPDOWN_CLASS_NAME = 'w-100 px-0 mb-1_5';

function StylePriceCheckerUI({
  vehicleParams,
  styles = null,
  tmvData = null,
  tmvAdjustment = null,
  initialPrices = null,
  onModeChange,
  hasChangeModel = true,
  showCalculators = false,
  isMobile = false,
  sharedDeal = null,
  resetSharedDealParams = null,
  updateVehicleParams,
  zipCode = null,
  isTestDeal = false,
  pageName = null,
  sessionId = null,
  vehicleData = null,
  closestDealers = null,
  isCore6830International = false,
}) {
  const { make, model, year, style } = vehicleParams;
  const hasInitialDefaultMsrp = !sharedDeal && MAKES_WITH_DEFAULT_MSRP.includes(get(make, 'slug'));
  const initialDefaultUserMsrp =
    get(initialPrices, 'userMsrp') || get(getTmvPrices(tmvData, isCore6830International), 'baseMSRP', 0);

  const [isChangeModel, toggleChangeModel] = useToggle(!(make && model && year));
  const [isDealRated, setDealRated] = useState(false);
  const [showTaxesFees, setShowTaxesFees] = useState(false);
  const [calculateTaxesFees, setCalculateTaxesFees] = useState(false);
  const [isGoToAppraiseModalOpen, toggleGoToAppraiseModal] = useToggle(false);
  const [defaultMsrp, setDefaultMsrp] = useState(0);
  const [chartPrices, setChartPrices] = useState({
    userDealerPrice: get(sharedDeal, 'userDealerPrice') || get(initialPrices, 'userDealerPrice') || 0,
    userMsrp: hasInitialDefaultMsrp ? initialDefaultUserMsrp : get(sharedDeal, 'userDealerPrice', 0),
  });
  const [dropdownErrors, setDropdownErrors] = useState({ errorMake: null, errorModel: null, errorYear: null });
  const [selectedDealer, setSelectedDealer] = useState(null);
  const [ratedDealType, setRatedDealType] = useState('');
  const [isDealSubmitted, setDealSubmitted] = useState(false);
  const [hasPriceErrors, setHasPriceErrors] = useState(false);
  const [isUsedYearSelected, setUsedYearSelected] = useState(false);

  const pricesFormRef = React.useRef();
  const priceChartRef = React.useRef();

  const isUsedMmy = (styles?.[0] && isUsedState(styles?.[0]?.publicationState)) || isUsedYearSelected;
  const appraisalLinkParams = year?.year
    ? objectToQueryString({
        make: make?.slug,
        model: model?.slug,
        year: year?.year,
      })
    : '';
  const appraisalLink = `/appraisal/?${appraisalLinkParams}`;

  useEffect(() => {
    if (isUsedMmy) {
      toggleGoToAppraiseModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUsedMmy]);

  const currentStyle = styles && (styles.find(({ id }) => id === get(style, 'id')) || styles[0]);

  useEffect(() => {
    if (!tmvData || chartPrices.userMsrp) {
      return;
    }

    const baseMsrp = get(getTmvPrices(tmvData, isCore6830International), 'baseMSRP', 0);

    startTransition(() => {
      setDefaultMsrp(baseMsrp);
      setChartPrices(previousChartPrices => ({
        ...previousChartPrices,
        userMsrp: MAKES_WITH_DEFAULT_MSRP.includes(get(vehicleParams, 'make.slug')) ? baseMsrp : 0,
      }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tmvData]);

  const resetPricesForm = useCallback(() => {
    pricesFormRef.current.reset();
    setDealRated(false);
    setShowTaxesFees(false);
    setCalculateTaxesFees(false);
    setChartPrices({ userMsrp: 0, userDealerPrice: 0 });
    setSelectedDealer(null);
    setDealSubmitted(false);
  }, []);

  const resetDropdownErrors = useCallback(() => {
    setDropdownErrors({ errorMake: null, errorModel: null, errorYear: null });
  }, []);

  const onStyleSelect = useCallback(
    newStyle => {
      updateVehicleParams({ style: newStyle });
      resetPricesForm();
    },
    [updateVehicleParams, resetPricesForm]
  );

  const onMmyDropdownSelect = useCallback(
    (newMake, newModel = null, newYear = null) => {
      resetDropdownErrors();
      resetPricesForm();

      if (get(newYear, 'year') === USED_YEAR) {
        setUsedYearSelected(true);
        updateVehicleParams({ make: newMake, model: newModel, year: null, style: null });
        return;
      }

      setUsedYearSelected(false);
      updateVehicleParams({ make: newMake, model: newModel, year: newYear, style: null });
    },
    [resetDropdownErrors, resetPricesForm, updateVehicleParams]
  );

  const onChangeDealer = useCallback(newDealer => {
    setSelectedDealer(newDealer);
    setDealSubmitted(false);
  }, []);

  const submitDeal = useCallback(
    chartData => {
      const dealerId = get(selectedDealer, 'id');

      if (dealerId !== 1) {
        EventToolbox.fireCustomEvent(PRICE_CHECKER_EVENTS.LIST_VIEW, {
          additionalData: vehicleData,
          vehicle: { ...vehicleParams, style: currentStyle },
          prices: chartPrices,
          ratedDealType: get(chartData, 'dealerPrice.label', '') || ratedDealType,
          selectedDealer,
        });

        EventToolbox.fireCustomEvent(PRICE_CHECKER_EVENTS.SUBMIT, {
          subactionName: 'attempt_submit',
        });

        submitShareDeal({
          vehicle: { ...vehicleParams, style: currentStyle, msrp: get(chartPrices, 'userMsrp') },
          deal: {
            dealType: get(chartData, 'dealerPrice.label', '') || ratedDealType,
            dealerPrice: get(chartPrices, 'userDealerPrice'),
            zipCode,
            pageName,
          },
          dealer: selectedDealer,
          isTestDeal,
          sessionId,
        });

        setDealSubmitted(true);
      }
    },
    [
      chartPrices,
      currentStyle,
      isTestDeal,
      pageName,
      ratedDealType,
      selectedDealer,
      sessionId,
      vehicleData,
      vehicleParams,
      zipCode,
    ]
  );

  const updateChartPrices = useCallback(
    userChartPrices => {
      if (!isDealSubmitted && isEqual(chartPrices, userChartPrices)) {
        submitDeal();
      } else {
        setCalculateTaxesFees(false);
        startTransition(() => setChartPrices(userChartPrices));
      }
    },
    [chartPrices, isDealSubmitted, submitDeal]
  );

  const allowCalculateTaxesFees = useDebounce(() => setCalculateTaxesFees(true), 2000);

  const rateDeal = useCallback(
    ({ tmvChartData }) => {
      startTransition(() => {
        setDealRated(true);
        setShowTaxesFees(true);
        setRatedDealType(get(tmvChartData, 'dealerPrice.label', ''));
      });
      allowCalculateTaxesFees();

      const canScroll = isMobile && !!priceChartRef.current && !sharedDeal;

      if (canScroll) {
        setTimeout(() => priceChartRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      }

      if (!!selectedDealer && !hasPriceErrors && !isDealSubmitted) {
        submitDeal(tmvChartData);
      }
    },
    [allowCalculateTaxesFees, hasPriceErrors, isDealSubmitted, isMobile, selectedDealer, sharedDeal, submitDeal]
  );

  const setUserDealerPrice = useCallback(
    userDealerPrice => {
      if (!!sharedDeal && !!resetSharedDealParams) {
        resetSharedDealParams();
      }

      setCalculateTaxesFees(false);
      allowCalculateTaxesFees();
      startTransition(() => setChartPrices({ ...chartPrices, userDealerPrice }));
    },
    [allowCalculateTaxesFees, chartPrices, resetSharedDealParams, sharedDeal]
  );

  const toggleMmySelect = useCallback(() => {
    if ((make && model && year) || !isChangeModel) {
      toggleChangeModel();
      return;
    }

    setDropdownErrors({
      errorMake: make ? null : DROPDOWN_ERRORS.MAKE,
      errorModel: model ? null : DROPDOWN_ERRORS.MODEL,
      errorYear: DROPDOWN_ERRORS.YEAR,
    });
  }, [isChangeModel, toggleChangeModel, make, model, year]);

  const dropdownsProps = useMemo(
    () => ({
      pubStates: [PUB_STATES.NEW, PUB_STATES.NEW_USED],
      tracking: {
        makeSelectTrackingId: 'pc_make_select',
        modelSelectTrackingId: 'pc_model_select',
        yearSelectTrackingId: 'pc_year_select',
      },
      vehicle: { make, model, year: isUsedMmy ? USED_YEAR : get(year, 'year') },
      additionalYearOptions: [{ year: USED_YEAR }],
    }),
    [isUsedMmy, make, model, year]
  );

  const calculatorProps = useMemo(
    () => ({
      tmvData,
      style: currentStyle && {
        id: currentStyle.id,
        makeName: currentStyle.makeName,
        modelName: currentStyle.modelName,
        trimName: currentStyle.trimName,
        year: currentStyle.year,
      },
    }),
    [currentStyle, tmvData]
  );
  const taxesFeesChartPrices = useMemo(
    () => (calculateTaxesFees ? { ...chartPrices } : {}),
    [calculateTaxesFees, chartPrices]
  );

  return (
    <Fragment>
      <Row className="style-price-checker pb-2">
        <Col xs={12} md={5} className="pe-lg-1">
          <ModeSelect onModeChange={onModeChange} activeMode={VEHICLE_SELECT_MODE_MAP.MMY} />
          <div className={classnames({ 'd-none': !isChangeModel })}>
            <VehicleDropdowns
              hasModel
              hasYear
              makeLabel="Make"
              modelLabel="Model"
              yearLabel="Year - new vehicles"
              labelClassName="small text-gray-darker mb-0_25"
              onDropdownsSelect={onMmyDropdownSelect}
              showButtonGroup={false}
              makeDropdownClassNames={VEHICLE_DROPDOWN_CLASS_NAME}
              modelDropdownClassNames={VEHICLE_DROPDOWN_CLASS_NAME}
              yearDropdownClassNames={VEHICLE_DROPDOWN_CLASS_NAME}
              hasSeparateLines
              isStyledSelects
              {...dropdownsProps}
              {...dropdownErrors}
            />
          </div>
          {!isChangeModel && (
            <div className="vehicle-name heading-5 mb-1">
              {year.year} {make.name} {model.name}
            </div>
          )}
          {(hasChangeModel || isUsedMmy) && (
            <div className="d-flex flex-column align-items-start gap-1 mb-1_5">
              {hasChangeModel && (
                <Button
                  color="link"
                  onClick={toggleMmySelect}
                  className="toggle-mmy-select-btn text-primary-darker text-transform-none p-0 border-0"
                >
                  {isChangeModel ? 'Close change model' : 'Change model'}
                </Button>
              )}
              {isUsedMmy && (
                <div className="text-danger small">
                  Sorry, we don’t have pricing for this vehicle.{' '}
                  <Link to={appraisalLink} className="text-blue-50" data-tracking-id="appraisal_link_tool_select">
                    Go to Edmunds used car appraiser
                  </Link>
                </div>
              )}
            </div>
          )}
          {isMobile && isChangeModel && <hr className="mt-0 mb-1_5" />}
          <StyledStyleDropdown
            make={make}
            model={model}
            year={isUsedMmy ? null : year}
            labelText="Style"
            labelClassName="small text-gray-darker mb-0_25"
            value={get(currentStyle, 'slug')}
            onChange={onStyleSelect}
            wrapperClassName="mb-1_5"
            data-tracking-id="pc_style_select"
          />
          <PricesForm
            chartPrices={chartPrices}
            setChartPrices={updateChartPrices}
            innerRef={pricesFormRef}
            defaultMsrp={defaultMsrp}
            isDealRated={isDealRated}
            disabled={!tmvData}
            isMobile={isMobile}
            resetSharedDealParams={resetSharedDealParams}
            mode={VEHICLE_SELECT_MODE_MAP.MMY}
            onChangeDealer={onChangeDealer}
            sharedWithCommunityProps={{
              handlePriceErrors: setHasPriceErrors,
              selectedDealer,
              isDealSubmitted,
              closestDealers,
              chartPrices,
              ratedDealType,
              isDealRated,
            }}
            isCore6830International={isCore6830International}
          />
        </Col>
        <Col xs={12} md={7} className="pt-md-0 ps-lg-1 pt-1_5">
          {(DEALERLESS_OEM.includes(get(make, 'slug')) || DEALERLESS_OEM_TRIAL.includes(get(make, 'slug'))) &&
          chartPrices.userDealerPrice ? (
            <PriceCheckerInfo
              makeName={make.name}
              modelName={model.name}
              year={year.year}
              userMsrp={chartPrices.userMsrp}
            />
          ) : (
            <Fragment>
              <PriceCheckerChart
                {...chartPrices}
                styleParams={{
                  styleId: get(currentStyle, 'id'),
                  styleName: get(currentStyle, 'name'),
                  makeName: get(make, 'name'),
                  modelName: get(model, 'name'),
                }}
                tmvData={tmvData}
                tmvAdjustment={tmvAdjustment}
                isMobile={isMobile}
                setDealRated={rateDeal}
                setUserDealerPrice={setUserDealerPrice}
                sharedDeal={sharedDeal}
                mode={VEHICLE_SELECT_MODE_MAP.MMY}
                priceChartRef={priceChartRef}
                isCore6830International={isCore6830International}
              />

              <PriceCheckerTaxesFees
                styleId={get(currentStyle, 'id')}
                tmvData={tmvData}
                showTaxesFees={showTaxesFees}
                calculateTaxesFees={calculateTaxesFees}
                tmvAdjustment={tmvAdjustment}
                isDealRated={isDealRated}
                sharedDeal={sharedDeal}
                calculatorProps={calculatorProps}
                isMobile={isMobile}
                showCalculators={showCalculators}
                incentivesLink={getModelIncentivesLink(
                  get(make, 'slug'),
                  get(model, 'slug'),
                  get(year, 'year'),
                  get(currentStyle, 'id')
                )}
                mode={VEHICLE_SELECT_MODE_MAP.MMY}
                {...taxesFeesChartPrices}
                isCore6830International={isCore6830International}
              />
            </Fragment>
          )}
        </Col>
      </Row>
      <GoToAppraiseModal isOpen={isGoToAppraiseModalOpen} toggle={toggleGoToAppraiseModal} />
    </Fragment>
  );
}

StylePriceCheckerUI.propTypes = {
  updateVehicleParams: PropTypes.func.isRequired,
  onModeChange: PropTypes.func.isRequired,
  vehicleParams: PropTypes.shape({
    make: PropTypes.shape({
      name: PropTypes.string,
      slug: PropTypes.string,
    }),
    model: PropTypes.shape({
      name: PropTypes.string,
      slug: PropTypes.string,
    }),
    year: PropTypes.shape({
      year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    style: PropTypes.shape({
      id: PropTypes.number,
    }),
  }).isRequired,
  initialPrices: PropTypes.shape({
    userDealerPrice: PropTypes.number,
    userMsrp: PropTypes.number,
  }),
  leadFormCreativeId: PropTypes.string,
  tmvData: VehicleEntities.NewTmv,
  tmvAdjustment: VehicleEntities.NewTmvAdjustment,
  styles: VehicleEntities.Styles,
  hasChangeModel: PropTypes.bool,
  hasEmbeddedLeadForm: PropTypes.bool,
  showCalculators: PropTypes.bool,
  isMobile: PropTypes.bool,
  zipCode: PropTypes.string,
  sharedDeal: PriceCheckerEntities.SharedDataEntity,
  resetSharedDealParams: PropTypes.func,
  visitorId: PropTypes.string,
  closestDealers: PropTypes.arrayOf(PropTypes.shape({})),
  isTestDeal: PropTypes.bool,
  pageName: PropTypes.string,
  vehicleData: PropTypes.shape({
    id: PropTypes.number,
    modelYearId: PropTypes.number,
    make: PropTypes.shape({
      id: PropTypes.number,
    }),
    model: PropTypes.shape({
      modelLinkCode: PropTypes.string,
    }),
    submodels: PropTypes.shape({
      slug: PropTypes.string,
    }),
  }),
  sessionId: PropTypes.string,
  isCore6830International: PropTypes.bool,
};

export const StylePriceChecker = connectToModel(StylePriceCheckerUI, {
  tmvData: bindToPath(
    ({ vehicleParams: { style }, styles }) =>
      !!(style || get(styles, '[0]')) && buildNewTmvPricingPath((style || styles[0]).id),
    VehicleModel
  ),
  tmvAdjustment: bindToPath(
    ({ vehicleParams: { style }, styles, isCore6830International }) =>
      !!(style || get(styles, '[0]')) && !isCore6830International && buildNewTmvAdjustmentPath((style || styles[0]).id),
    VehicleModel
  ),
  closestDealers: bindToPath(
    ({ vehicleParams: { make, model, year } }) =>
      !!(make && model && year) && buildClosestDealersPath({ make: make.slug }),
    DealershipsModel
  ),
  vehicleData: bindToPath(({ vehicleParams }) => {
    const make = get(vehicleParams, 'make.slug');
    const model = get(vehicleParams, 'model.slug');
    const year = get(vehicleParams, 'year.year');
    return make && model && year && buildVehiclePath({ make, model, year });
  }, VehicleModel),
});
